import React, { useRef, useEffect, useState } from 'react';
import { Place } from './MapBetweenPlaces.tsx';
import HubPinSvg from './HubPinSvg.tsx';

interface PlacesListProps {
  places: Place[];
  eventPlaces: Place[];
  likedPlaces: string[];
  setLikedPlaces: React.Dispatch<React.SetStateAction<string[]>>;
  markers: any[];
  eventMarkers: any[];
  hubGemMarkers: any[];
  selectedPlaceRef: React.MutableRefObject<Place | null>;
  selectedPlace: Place | null;
  setSelectedPlace: React.Dispatch<React.SetStateAction<Place | null>>;
  placeRefs: React.MutableRefObject<{ [key: string]: HTMLLIElement | null }>;
  location1LL: { lat: number; lng: number } | null;
  calculateDistance: (loc1: { lat: number; lng: number }, loc2: { lat: number; lng: number }) => number;
  expandedDescriptions: { [key: string]: boolean };
  toggleDescription: (placeId: string) => void;
  expandedReviews: { [key: string]: boolean };
  toggleReview: (placeId: string) => void;
  focusMapOnMarker: (marker: any, place?: any) => void;
  displayedTitle: string;
  ensurePlaceMarkersVisible: () => Promise<void>;
  handleShare: () => void;
}

// Show Halfway Label
const SHOW_LIGHT_PLACE_DETAIL = false;
	
const getShortDisplayAddress = (title?: string, address?: string): string => {

  if (!address) return ''; 
  
  // Check if the address starts with the title followed by a comma
  if (title &&  address.startsWith(`${title},`)) {
    // Remove the title and the following comma
    address = address.slice(title.length + 1).trim();
  }

  // Assuming the address format is "Street, City, Postcode, Country"
  const parts = address.split(',');
  if (parts.length > 2) {
    // Remove the last two parts (postcode and country)
    return parts.slice(0, -1).join(',').trim();
  }
  return address.trim();
};

const shortenAddressToFit = (address: string, containerWidth: number, categoryWidth: number, distanceWidth: number, fontSize: number): string => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.font = `${fontSize}px Arial`;

  let shortenedAddress = address;
  let textWidth = ctx.measureText(shortenedAddress).width;

  //console.log(`check Address fit: ${address}, width: ${textWidth} Container Width: ${containerWidth}, Category Width: ${categoryWidth}, Distance Width: ${distanceWidth}, Font Size: ${fontSize}`);
  
  const outsidePaddingReduction = 150;
  let trimmed = false;
  while (textWidth > (containerWidth - categoryWidth - outsidePaddingReduction - distanceWidth) && shortenedAddress.includes(',')) {
    const parts = shortenedAddress.split(',');
    shortenedAddress = parts.slice(0, -1).join(',').trim();
    textWidth = ctx.measureText(shortenedAddress).width;
    //console.log(`Will shorten Address: ${shortenedAddress}, Text Width: ${textWidth}, Required Width: ${containerWidth - categoryWidth - distanceWidth}`);
    trimmed = true;
  }

  return shortenedAddress;
};

const shortenTitleToFit = (title: string, containerWidth: number, fontSize: number): string => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.font = `${fontSize}px Arial`;

  let shortenedTitle = title;
  let textWidth = ctx.measureText(shortenedTitle).width;
  const outsidePaddingReduction = 150;
  //console.log(`shortenTitleToFit check: ${title}, Text Width: ${textWidth}, Avail Width: ${containerWidth - outsidePaddingReduction}`);
  
  while (textWidth > (containerWidth - outsidePaddingReduction) && shortenedTitle.length > 0) {
    shortenedTitle = shortenedTitle.slice(0, -1).trim();
    textWidth = ctx.measureText(shortenedTitle).width;
    //console.log(`Will shorten Title: ${shortenedTitle}, Text Width: ${textWidth}, Required Width: ${containerWidth - outsidePaddingReduction}`);
  }

  return shortenedTitle;
};

const PlacesList: React.FC<PlacesListProps> = ({
  places,
  eventPlaces,
  likedPlaces,
  setLikedPlaces,
  markers,
  eventMarkers,
  hubGemMarkers,
  selectedPlaceRef,
  selectedPlace,
  setSelectedPlace,
  placeRefs,
  location1LL,
  calculateDistance,
  expandedDescriptions,
  toggleDescription,
  expandedReviews,
  toggleReview,
  focusMapOnMarker,
  displayedTitle,
  ensurePlaceMarkersVisible,
  handleShare,
}) => {
  
	const containerRef = useRef<HTMLDivElement>(null);
	const itemRef = useRef<HTMLLIElement>(null);
	const [containerWidth, setContainerWidth] = useState(0);
	const [itemWidth, setItemWidth] = useState(0);
	const [shortenedData, setShortenedData] = useState<{ [key: string]: { title: string; address: string } }>({});
	const screenWidth = window.innerWidth;
	const fontSize = screenWidth <= 768 ? 11 : 12;

	const PLACE_TYPE_THEMES = {
	  'AI_GEM': {
		bg: '#38CEF5',    
		border: '#01A2DE', // The darker blue border
		font: 'black'
	  },
	  'EV Charging': { bg: '#6bc9ff', border: '#b0b0b0', font: '#ffffff' },
	  'Petrol Station': { bg: '#16B0F9', border: 'black', font: 'black' },
	  'Highway Services': { bg: '#16B0F9', border: 'black', font: 'black' },
	  'Event Venue': { bg: '#16B0F9', border: '#0b6fa8', font: 'black' },
	  'DEFAULT': { bg: '#00e64d', border: 'black', font: 'black' }
	};
    
	const handleLikeToggle = (event: React.MouseEvent, placeId: string) => {
		event.stopPropagation(); 
		if (likedPlaces.includes(placeId)) {
		  setLikedPlaces(likedPlaces.filter((id) => id !== placeId)); // Update likedPlaces
		} else {
		  setLikedPlaces([...likedPlaces, placeId]); // Update likedPlaces
		}
	};

	const createListIcon = (number) => {
	  const canvas = document.createElement('canvas');
	  canvas.width = 24;
	  canvas.height = 24;
	  const ctx = canvas.getContext('2d');

	  // Draw the circle
	  ctx.beginPath();
	  ctx.arc(12, 12, 9, 0, 2 * Math.PI);
	  ctx.fillStyle = '#00e64d';
	  ctx.fill();

	  // Draw the border
	  ctx.strokeStyle = 'black';
	  ctx.lineWidth = 1;
	  ctx.stroke();

	  // Draw the text
	  ctx.fillStyle = 'black';
	  ctx.font = '10px Arial';
	  ctx.textAlign = 'center';
	  ctx.textBaseline = 'middle';

	  // Check if number is the string "undefined"
	  const displayText = number === "undefined" ? '' : number;

	  ctx.fillText(displayText, 12, 12);

	  return canvas.toDataURL();
	};

  const createGoogleMapsLink = (title: string, address: string) => {
    const query = encodeURIComponent(`${title}, ${address}`);
    //console.log('place query='+query);
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
    if (itemRef.current) {
      setItemWidth(itemRef.current.offsetWidth);
    }
    
    if (itemRef.current && itemRef.current.offsetWidth > 0) {
      //console.log('places item width known '+itemRef.current.offsetWidth.length);
      const newShortenedData: { [key: string]: { title: string; address: string; distanceText: string } } = {};
      places.forEach((place) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        ctx.font = `${fontSize}px Arial`;

		let categoryWidth = 0;
		if (place.categories && place.categories.length > 0 && place.categories[0].name) {
		    categoryWidth = ctx.measureText(place.categories[0].name).width;
		} else {
		    console.warn('Category name is undefined or empty for place:', place);
		}
        
        const miles = calculateDistance(location1LL, place.position);
        const kilometers = Math.round(miles * 1.60934);
        const distanceText = `${kilometers} km`;
        const distanceWidth = ctx.measureText(distanceText).width;

        const shortAddress = getShortDisplayAddress(place.title, place.address);
        const shortenedAddress = shortenAddressToFit(shortAddress, itemRef.current.offsetWidth, categoryWidth, distanceWidth, fontSize);
        const shortenedTitle = shortenTitleToFit(place.title, itemRef.current.offsetWidth, fontSize);

        newShortenedData[place.place_id] = { title: shortenedTitle, address: shortenedAddress, distanceText };
      });
      setShortenedData(newShortenedData);
    }
  }, [containerRef, itemRef, places]);

  const combinedPlaces = [...places, ...eventPlaces];
  
  return (
    <div className="places-overlay">
      <div className="places-overlay-content" ref={containerRef}>
		{displayedTitle && (
		<div className="socialshare-header-wrapper">
          <h4 className={`sociallist-title ${SHOW_LIGHT_PLACE_DETAIL ? 'is-light' : ''}`}>
            {displayedTitle}
            <br />
            <span className="subtitle-text">Convenient Places To Consider</span>
          </h4>          
		  <div className="socialshare-button-group" onClick={handleShare}>
			<img 
			  src="/sharesocialicon.png" 
			  alt="Share" 
			  className="socialshare-icon-image" 
			/>
			<span className="socialshare-text">SHARE</span>
		  </div>
        </div>
		)}
        {combinedPlaces.length > 0 ? (
          <ul className="places-list">
            {combinedPlaces.map((place) => {
              
				const shortAddress = getShortDisplayAddress(place.title, place.address);
				const shortenedAddress = shortenedData[place.place_id]?.address || shortAddress;
				const shortenedTitle = shortenedData[place.place_id]?.title || place.title;
				const distanceText = shortenedData[place.place_id]?.distanceText || `(${Math.round(calculateDistance(location1LL, place.position) * 1.60934)} km)`;

				var categoryText = place.categories?.[0]?.name;

				var strapLine = place.strapLine;
              
				const type = place.placeType || '';
				const isHub = !!place.is_hub;

				// 1. Get the theme from the map or use the default
				const theme = PLACE_TYPE_THEMES[type] || PLACE_TYPE_THEMES['DEFAULT'];

				const isCustomColorType = isHub || type === 'AI_GEM';

				// 3. Resolve colors
				const backgroundColor = (isCustomColorType && place.fillColor) ? place.fillColor : theme.bg;
				const borderColor = (isCustomColorType && place.strokeColor) ? place.strokeColor : theme.border;
				const fontColor = theme.font;
                  
              return (

              <li
                key={place.place_id}
                ref={(el) => {
                  if (el) {
                    placeRefs.current[place.place_id] = el;
                      if (!itemRef.current) {
                        itemRef.current = el;
                      }                    
                  }
                }}
				className={`place-item 
					${SHOW_LIGHT_PLACE_DETAIL ? 'is-light' : ''} 
					${selectedPlace?.place_id === place.place_id ? 'selected' : ''}`
				}			
                onClick={async () => {
				  setSelectedPlace(place);
				  
					if (!place.is_hub) {
					  try {
						await ensurePlaceMarkersVisible();
					  } catch (e) {
						console.warn('Failed to ensure markers:', e);
						return;
					  }
					}				  
				  
				  // 1. Combine all marker arrays into one search pool
				  // We use filter(Boolean) to ensure we don't try to call .getData() on null/undefined markers
				  const allMarkers = [
					...(markers || []),
					...(eventMarkers || []),
					...(hubGemMarkers || [])
				  ].filter(Boolean);

				  // 2. Find the marker matching the current place ID
				  const marker = allMarkers.find(
					(m) => m.getData() === place.id
				  );
	
				  if (marker) {
					focusMapOnMarker(marker,place);
				  }                  
                }}
              >
				<div className="title-distance-container">
				  <div style={{ display: 'flex', alignItems: 'center' }}>
					<div
					  style={{
						width: '17px',
						height: '17px',
						backgroundColor: backgroundColor,
						borderRadius: '50%',
						border: `1px solid ${borderColor}`,
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						marginRight: '10px',
					  }}
					>
					  {(place.placeType === 'EV Charging') ? (
						<svg
						  width="12"
						  height="12"
						  viewBox="0 0 24 24"
						  xmlns="http://www.w3.org/2000/svg"
						  style={{
							fill: 'white',
							display: 'block',
						  }}
						>
						  <path d="M13 2L3 14h7v8l10-12h-7z" />
						</svg>
					  ) : (
						<span
						  style={{
							fontSize: '11px',
							fontFamily: 'Arial',
							color: fontColor,
							fontWeight: 'bold',
						  }}
						>
						  {place.map_id}
						</span>
					  )}
					</div>

				  <img
					src={likedPlaces.includes(place.place_id) ? 'sh-heart-on.png' : 'sh-heart-off.png'}
					alt="Like"
					className="like-icon"
					onClick={(event) => handleLikeToggle(event, place.place_id)}
				  />
					<h5>{shortenedTitle}</h5>
				  </div>
					<div>
					  {/* Category Badge - Always at the top if it exists */}
					  {place.categories?.length > 0 && (
						<span className={`place-category ${place.placeType === 'AI_GEM' ? 'ai-pick' : ''}`}>
						  {categoryText}
						</span>
					  )}
						{location1LL && place.position && (
						  <div className="list-item-meta">
							{(() => {
							  // --- CASE 1: CURATED CONTENT (Hubs and AI Gems) ---
							  if (place.is_hub || place.placeType === 'AI_GEM' || place.placeType === 'CURATED_PICK') {
								return (
								  <>
									<p className="description-strapline">
									  {place.strapLine}
									</p>
									<span className="km-distance">
									  {place.town ? `${place.town} • ` : ''}{distanceText}
									</span>
								  </>
								);
							  }

							  // --- CASE 2: STANDARD CONTENT ---
							  return (
								<>
								  <p className="address-line">{shortenedAddress}</p>
								  <span className="km-distance">
									{distanceText}
								  </span>
								</>
							  );
							})()}
						  </div>
						)}

					</div>
				</div>

                {!place.limit_detail && place.photos && place.photos.length > 0 && (
                  <div className="carousel">
                    {place.photos.map((photo, index) => (
                      <img key={index} src={photo.link} alt={photo.title} className={`photo-box ${photo.orientation}`} />
                    ))}
                  </div>
                )}

                {!SHOW_LIGHT_PLACE_DETAIL && (
                  <div className="place-moreinfo-container">
                    <a href={createGoogleMapsLink(place.title, place.address)} target="_blank" rel="noopener noreferrer">
                      more
                    </a>
                  </div>
                )}
              </li>
              );
            })}

          </ul>
        ) : (
          <p>No nearby places found.</p>
        )}
      </div>
    </div>
  );
};

export default PlacesList;